import { useEffect, useState } from "react";
import { api, apiErr, fmtINR, fmtDate, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { HandCoins, Download, Loader2 } from "lucide-react";

export default function OwnerHandover() {
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ amount: "", handed_to: "", branch_code: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  async function refresh() {
    const [s, l, b] = await Promise.all([
      api.get("/dashboard/summary"),
      api.get("/offloads", { params: { offload_type: "OWNER_HANDOVER" } }),
      api.get("/meta/branches"),
    ]);
    setSummary(s.data); setRows(l.data); setBranches(b.data);
  }
  useEffect(() => { refresh(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Enter amount");
    if (!form.handed_to) return toast.error("Handed-to name required");
    setBusy(true);
    try {
      const { data } = await api.post("/offloads", {
        ...form,
        amount: parseFloat(form.amount),
        offload_type: "OWNER_HANDOVER",
      });
      setCreated(data);
      toast.success(`Handover voucher ${data.offload_no} created`);
      setForm({ amount: "", handed_to: "", branch_code: "", remarks: "" });
      await refresh();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="handover-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900">Cash Handover to Owner</h1>
          <p className="text-slate-500 mt-1">Generate a signed acknowledgement for cash handed to the owner / director.</p>
        </div>
        {summary && (
          <div className="stat-card-primary min-w-[240px]">
            <div className="text-xs uppercase text-blue-100 tracking-wider">Cash in Hand</div>
            <div className="text-2xl font-extrabold mono mt-1">{fmtINR(summary.cash_in_hand)}</div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><HandCoins className="h-5 w-5 text-amber-600" /> New Owner Handover</h3>
          <Field label="Amount (₹)" required>
            <input type="number" step="0.01" min="0" required data-testid="ho-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Handed To (name)" required>
              <input data-testid="ho-name" required value={form.handed_to} onChange={(e) => setForm({ ...form, handed_to: e.target.value })} className={inputCls} placeholder="Mr. Ratan (Owner)" />
            </Field>
            <Field label="From Branch (optional)">
              <select value={form.branch_code} onChange={(e) => setForm({ ...form, branch_code: e.target.value })} className={inputCls}>
                <option value="">— Head Office —</option>
                {branches.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Remarks"><textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className={inputCls} /></Field>
          <button type="submit" disabled={busy} data-testid="ho-submit" className="w-full py-3 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Generate Handover Voucher
          </button>
        </form>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-800 mb-4">Recent Handovers</h3>
          {created && (
            <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-center justify-between">
              <div><div className="font-bold mono text-amber-900">{created.offload_no}</div><div className="text-xs text-amber-800">{fmtINR(created.amount)} · {created.handed_to}</div></div>
              <a href={`${API_BASE}/offloads/${created.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold"><Download className="h-3 w-3" /> PDF</a>
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {rows.length === 0 && <div className="py-6 text-center text-slate-400 text-sm">No handovers yet</div>}
            {rows.slice(0, 15).map((r) => (
              <div key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold mono text-slate-800">{r.offload_no}</div>
                  <div className="text-xs text-slate-500">{r.handed_to} · {fmtDate(r.created_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="mono font-bold text-slate-800">{fmtINR(r.amount)}</div>
                  <a href={`${API_BASE}/offloads/${r.id}/pdf`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 font-semibold text-slate-700">PDF</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-900 bg-white";
function Field({ label, required, children }) {
  return (<label className="block"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label} {required && <span className="text-red-500">*</span>}</div>{children}</label>);
}
